import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  ORACLE_CARDS_FALLBACK, 
  CATEGORY_DISPLAY_MAP, 
  EXPECTED_CARD_COUNT,
  type OracleCardData 
} from '@/data/oracleCardsFallback';

/**
 * Shared Oracle Card interface for UI components
 * Maps database/fallback data to component-friendly structure
 */
export interface OracleCard {
  id: string;
  title: string;
  suit: string;
  messageFromMirror: string;
  whatThisReflects: string;
  healingInvitation: string;
  affirmation: string;
  keywords: string[];
  category: string;
}

/**
 * Maps a category string to display suit format
 */
function getCategorySuit(category: string): string {
  return CATEGORY_DISPLAY_MAP[category.toLowerCase()] || category;
}

/**
 * Maps database/fallback card data to UI component format
 */
function mapCardToUIFormat(card: OracleCardData, index: number): OracleCard {
  return {
    id: card.id || `card-${index}`,
    title: card.title,
    suit: getCategorySuit(card.category),
    messageFromMirror: card.message,
    whatThisReflects: card.whatThisReflects || 'This card reflects an aspect of your inner landscape that is seeking attention and integration.',
    healingInvitation: card.healingInvitation || 'Take a moment to reflect on how this message applies to your current situation.',
    affirmation: card.affirmation || 'I am open to the wisdom this card offers.',
    keywords: card.keywords || [],
    category: card.category,
  };
}

/**
 * Shared hook for fetching oracle cards from database with fallback
 * 
 * This is the single source of truth for oracle card data across all components.
 * Uses the oracle_cards Supabase table as primary source.
 * Falls back to ORACLE_CARDS_FALLBACK if database is unavailable or returns unexpected data.
 */
export function useOracleCardsData() {
  const [cards, setCards] = useState<OracleCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('oracle_cards')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      if (!data || data.length === 0) {
        console.warn('[useOracleCardsData] No cards returned from database, using fallback');
        setCards(ORACLE_CARDS_FALLBACK.map(mapCardToUIFormat));
        setIsUsingFallback(true);
        return;
      }

      // Sanity check: expect 55 cards
      if (data.length !== EXPECTED_CARD_COUNT) {
        console.warn(
          `[useOracleCardsData] Expected ${EXPECTED_CARD_COUNT} cards, got ${data.length}. ` +
          `This may indicate missing or extra cards in the database.`
        );
      }

      // Map database cards to UI format
      const mappedCards: OracleCard[] = data.map((dbCard, index) => ({
        id: dbCard.id,
        title: dbCard.title,
        suit: getCategorySuit(dbCard.category || 'spiritual'),
        messageFromMirror: dbCard.message || '',
        whatThisReflects: 'This card reflects an aspect of your inner landscape that is seeking attention and integration.',
        healingInvitation: 'Take a moment to reflect on how this message applies to your current situation.',
        affirmation: 'I am open to the wisdom this card offers.',
        keywords: dbCard.keywords || [],
        category: dbCard.category || 'spiritual',
      }));

      setCards(mappedCards);
      setIsUsingFallback(false);
    } catch (err) {
      console.error('[useOracleCardsData] Failed to fetch cards from database:', err);
      setError(err instanceof Error ? err : new Error('Failed to load oracle cards'));
      
      // Use fallback on error
      console.warn('[useOracleCardsData] Using fallback data due to error');
      setCards(ORACLE_CARDS_FALLBACK.map(mapCardToUIFormat));
      setIsUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  return { 
    cards, 
    loading, 
    error, 
    isUsingFallback,
    refetch: loadCards,
    expectedCardCount: EXPECTED_CARD_COUNT,
  };
}
